/**
 * PR-CHECKOUT-UI-CANONICAL-STATES-V1
 *
 * SOLE canonical mapping authority for backend runtime_state → UI rendering category.
 *
 * Authority: ADR-098 (Canonical Checkout Runtime States)
 *
 * All runtime_state interpretation MUST go through this module.
 * No other file may compare runtime_state strings directly against state literals.
 * Architecture test CHKUI-001 and CHKUI-002 enforce this invariant.
 *
 * ADR-098 precedence rule:
 *   runtime_state is authoritative when non-null.
 *   session.status is the legacy fallback for pre-migration sessions (runtime_state = null).
 */

// ── Types ─────────────────────────────────────────────────────────────────────

export type CanonicalRuntimeState =
  | 'open'
  | 'creating_intent'
  | 'awaiting_selection'
  | 'awaiting_customer_action'
  | 'awaiting_confirmation'
  | 'processing'
  | 'completed'
  | 'failed'
  | 'expired'
  | 'cancelled'
  | 'unknown';

/**
 * Rendering-oriented category returned by the adapter.
 * Reducer maps each category to a distinct UI state.
 */
export type RuntimeStateCategory =
  | 'terminal_success'
  | 'terminal_expired'
  | 'terminal_cancelled'
  | 'terminal_failed'
  | 'awaiting_customer_action'
  | 'awaiting_confirmation'
  | 'in_progress'
  | 'awaiting_selection'
  | 'unknown';

// ── Core mapping ──────────────────────────────────────────────────────────────

/**
 * Maps backend runtime_state + session.status → RuntimeStateCategory.
 *
 * ADR-098: runtime_state is authoritative when non-null.
 * Falls back to session.status for pre-migration sessions where runtime_state is null.
 */
export function categorizeState(
  runtimeState: string | null | undefined,
  sessionStatus: string,
): RuntimeStateCategory {
  const rs = runtimeState ?? null;

  // Terminal states — checked first; canonical overrides legacy status
  if (rs === 'completed' || (!rs && sessionStatus === 'completed')) return 'terminal_success';
  if (rs === 'expired'   || (!rs && sessionStatus === 'expired'))   return 'terminal_expired';
  if (rs === 'cancelled' || (!rs && sessionStatus === 'cancelled')) return 'terminal_cancelled';
  if (rs === 'failed') return 'terminal_failed';

  // In-progress states requiring distinct rendering
  if (rs === 'awaiting_customer_action') return 'awaiting_customer_action';
  if (rs === 'awaiting_confirmation')    return 'awaiting_confirmation';

  // Generic in-progress — session is alive and the backend is doing work
  if (rs === 'creating_intent' || rs === 'processing' || sessionStatus === 'payment_initiated') {
    return 'in_progress';
  }

  // Customer action needed at the selection level
  if (rs === 'awaiting_selection' || rs === 'open') return 'awaiting_selection';

  return 'unknown';
}

// ── Convenience helpers ───────────────────────────────────────────────────────

/** True for states where no further customer action is possible. */
export function isTerminalState(
  runtimeState: string | null | undefined,
  sessionStatus: string,
): boolean {
  const cat = categorizeState(runtimeState, sessionStatus);
  return (
    cat === 'terminal_success' ||
    cat === 'terminal_expired' ||
    cat === 'terminal_cancelled' ||
    cat === 'terminal_failed'
  );
}

/**
 * True for states where the browser should continue polling.
 * ADR-098: browser poll timeout does NOT make these states terminal.
 */
export function shouldContinuePolling(
  runtimeState: string | null | undefined,
  sessionStatus: string,
): boolean {
  const cat = categorizeState(runtimeState, sessionStatus);
  return (
    cat === 'in_progress' ||
    cat === 'awaiting_customer_action' ||
    cat === 'awaiting_confirmation'
  );
}
